import * as moment from 'moment';
import { App } from 'ionic-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { CalendarEvent } from 'angular-calendar';
import { ClientServiceProvider } from '../../../clients/services/client-service/client-service';
import { DataSource } from '../../interfaces/data-source.interface';
import { DisplayServiceProvider } from '../../../core/services/display-service/display-service';
import { FilterInput } from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/filter-input.interface';
import { FilterOutput } from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/filter-outpt.interface';
import { FiltersConfig } from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/filters-config.interface';
import { FilterServiceProvider, FilterType } from '../../../core/services/filter-service/FilterServiceProvider';
import { IClientModel } from '../../../clients/interfaces/i-client-model.interface';
import { Injectable } from '@angular/core';
import { PlayerServiceProvider } from '../../../core/services/player-service/player-service';
import { SellionsCalendarEvent } from '../../interfaces/sellions-calendar-event.interface';
import { SellionsEvent } from '../events-service/events-service';
import {
  FiltersService
} from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/services/filters/filters.service';
import {
  FilterSet
} from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/filter-set.interface';

@Injectable()
export class CalendarServiceProvider {
  private _selectedDate: BehaviorSubject<moment.Moment> = new BehaviorSubject<moment.Moment>(moment());
  public readonly selectedDate$: Observable<moment.Moment> = this._selectedDate.asObservable();

  private _currentEvents: BehaviorSubject<CalendarEvent[]> = new BehaviorSubject<CalendarEvent[]>([]);
  public readonly currentEvents$: Observable<CalendarEvent[]> = this._currentEvents.asObservable();

  private _loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly loading$: Observable<boolean> = this._loading.asObservable();

  private dataSources: DataSource[];
  private colors = {
    primary: '#2D3E50',
    secondary: '#979797',
  };

  private currentFilters: FilterOutput[];

  constructor(
    private app: App,
    private clientService: ClientServiceProvider,
    private displayService: DisplayServiceProvider,
    private filterServiceLegacy: FilterServiceProvider,
    private filterService: FiltersService,
    private playerService: PlayerServiceProvider,
  ) {
    this.filterServiceLegacy.subscribe(FilterType.CALENDAR, () => this._loading.next(false));
  }

  public setDataSources(sources: DataSource[]) {
    this.dataSources = sources;
    this.filterService.getFilters('calendar')
      .then(filterSet => {
        this.getFiltersConfig()
          .then(config => {
            filterSet.filterConfig = {
              label: '',
              filters: config
            }
            return this.filterService.applyFilters('calendar', filterSet);
          })
    });
  }

  public async getEvents(): Promise<SellionsCalendarEvent[]> {
    if (!this.dataSources) return Promise.resolve([]);

    return this.filterService.getFilters('calendar').then(async filterSet => {
      let filterSetCopy = filterSet ? JSON.parse(JSON.stringify(filterSet)) : {filters: []};
      let currentSelectedDate = this._selectedDate.getValue().clone().startOf('month');
      let currentSelectedDateNextMonth = currentSelectedDate.clone().add(1, 'month').startOf('month');
      filterSetCopy.filters = [...filterSetCopy.filters,
        {
          "column": "start_date",
          "value": currentSelectedDate.format("YYYY-MM-01"),
          "type": "STRING",
          "queryValue": {
            "column": "start_date",
            "type": "GREATER_OR_EQUALS",
            "value": currentSelectedDate.format("YYYY-MM-01")
          }
        },
        {
          "column": "end_date",
          "value": currentSelectedDateNextMonth.format("YYYY-MM-01"),
          "type": "STRING",
          "queryValue": {
            "column": "end_date",
            "type": "LESS_THAN",
            "value": currentSelectedDateNextMonth.format("YYYY-MM-01")
          }
        }
      ];
      const promises = this.dataSources.map((source) => {
        return source.filter(filterSetCopy);
      });
      const events = await Promise.all(promises);
      let sellionsCalendarEvents = this.flattenEvents(events);

      if((!filterSetCopy.filters || filterSetCopy.filters.length === 0) && (!filterSetCopy.searchQuery || filterSetCopy.searchQuery.length === 0)) {
        let rawEvents = sellionsCalendarEvents.map(e => e.dataSource.getRawEvent(e));
        this.filterService.extractOptionsFromData('calendar', rawEvents);
      }
      return sellionsCalendarEvents;
    });
  }

  public async setFilters(filters: FilterOutput[]): Promise<SellionsCalendarEvent[]> {
    if (!this.dataSources) return Promise.resolve([]);
    return this.filterService.applyFilters('calendar', filters)
      .then(() => this.getEvents());
  }

  public async search(query: string): Promise<SellionsCalendarEvent[]> {
    if (!this.dataSources) return Promise.resolve([]);
    return this.filterService.applySearch('calendar', query)
      .then(() => this.getEvents());
  }

  private flattenEvents(events: SellionsCalendarEvent[][]): SellionsCalendarEvent[] {
    return events.reduce((accumulator, value) => accumulator.concat(value), []);
  }

  public async getFiltersConfig(): Promise<FilterInput[]> {
    if (!this.dataSources) return Promise.resolve([]);
    const promises = this.dataSources.map((source) => source.getFiltersConfig());
    const filterConfigs = await Promise.all(promises);
    return this.flattenFilterConfig(filterConfigs);
  }

  private flattenFilterConfig(filterConfigs: FiltersConfig[]): FilterInput[] {
    return filterConfigs ? filterConfigs.reduce((accumulator, value) => accumulator.concat(value ? value.filters : []), []) : [];
  }

  public getFilters(): FilterOutput[] {
    return [].concat(...this.dataSources.map((source) => source.filters));
  }

  public mapEvents(events: SellionsCalendarEvent[]): CalendarEvent[] {
    return events.map((event) => ({
      start: new Date(event.dateFrom),
      end: moment(event.dateTo).isSame(moment(event.dateFrom).startOf('day')) ? moment(event.dateTo).add(-1, 'day').toDate() : new Date(event.dateTo),
      title: event.displayComponentData.title || '-',
      color: {
        primary: this.colors.primary,
        secondary: this.colors.secondary,
      },
    }));
  }

  public setColors(colors: { primary: string; secondary: string }) {
    this.colors = colors;
  }

  public nextDate(mode: 'forward' | 'backward', page: 'CalendarDayPage' | 'CalendarMonthPage'): void {
    if (this._loading.getValue() === true) {
      return;
    }

    let currentDate = this._selectedDate.getValue();
    currentDate.add(mode === 'forward' ? 1 : -1, page === 'CalendarDayPage' ? 'days' : 'months');
    this._selectedDate.next(currentDate);
  }

  public setDate(date: Date): void {
    this._selectedDate.next(moment(date));
  }

  public showEventDetail(event: SellionsEvent): void {
    const client: IClientModel = this.clientService.getById(event.clientId);
    this.app.getRootNav().push('EventsPage', { event, client, calendar: true });
  }
}



// WEBPACK FOOTER //
// ./src/modules/calendar/services/calendar-service/calendar-service.ts